import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Bell, Megaphone, Users, CheckCircle2, Clock, AlertTriangle, Search, X, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnnouncementsPage() {
  const { user, getAuthHeader } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewReadsOpen, setViewReadsOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementReads, setAnnouncementReads] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  
  // Multi-select for employees
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal',
    target_type: 'all',
    target_employees: []
  });

  const isAdmin = user?.is_owner || user?.allowed_modules?.includes('announcements');

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    const officeStaff = employees.filter(emp => emp.staff_type === 'Office Staff');
    if (!employeeSearch) return officeStaff;
    return officeStaff.filter(emp => 
      emp.name?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.employee_code?.toLowerCase().includes(employeeSearch.toLowerCase())
    );
  }, [employees, employeeSearch]);

  // Get selected employee names
  const selectedEmployeeNames = useMemo(() => {
    return formData.target_employees.map(id => {
      const emp = employees.find(e => e.user_id === id || e.id === id);
      return emp?.name || id;
    });
  }, [formData.target_employees, employees]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/announcements`, { headers: getAuthHeader() });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/hr-employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    if (isAdmin) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (formData.target_type === 'selected' && formData.target_employees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/announcements`, {
        title: formData.title,
        message: formData.message,
        priority: formData.priority,
        target_type: formData.target_type,
        target_employees: formData.target_employees
      }, { headers: getAuthHeader() });
      
      toast.success('Announcement created successfully');
      setCreateOpen(false);
      setFormData({ title: '', message: '', priority: 'normal', target_type: 'all', target_employees: [] });
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleAcknowledge = async (announcementId) => {
    try {
      await axios.post(`${API_URL}/announcements/acknowledge`, {
        announcement_id: announcementId
      }, { headers: getAuthHeader() });
      
      toast.success('Announcement acknowledged');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleDelete = async () => {
    if (!announcementToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/announcements/${announcementToDelete.id}`, { headers: getAuthHeader() });
      toast.success('Announcement deleted');
      setDeleteConfirmOpen(false);
      setAnnouncementToDelete(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const viewReads = async (announcement) => {
    setSelectedAnnouncement(announcement);
    try {
      const response = await axios.get(`${API_URL}/announcements/${announcement.id}/reads`, { headers: getAuthHeader() });
      setAnnouncementReads(response.data);
      setViewReadsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch read status');
    }
  };

  const toggleEmployeeSelection = (employeeUserId) => {
    setFormData(prev => {
      const isSelected = prev.target_employees.includes(employeeUserId);
      if (isSelected) {
        return { ...prev, target_employees: prev.target_employees.filter(id => id !== employeeUserId) };
      } else {
        return { ...prev, target_employees: [...prev.target_employees, employeeUserId] };
      }
    });
  };

  const removeSelectedEmployee = (employeeUserId) => {
    setFormData(prev => ({
      ...prev,
      target_employees: prev.target_employees.filter(id => id !== employeeUserId)
    }));
  };

  // Separate acknowledged and unacknowledged for employee view
  const unacknowledgedAnnouncements = announcements.filter(a => !a.acknowledged);
  const acknowledgedAnnouncements = announcements.filter(a => a.acknowledged);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcements-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Megaphone className="w-8 h-8 text-blue-600" />
              Announcements
            </h1>
            <p className="text-muted-foreground">
              {isAdmin ? 'Create and manage company announcements' : 'View and acknowledge company announcements'}
            </p>
          </div>
          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-announcement-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Announcement</DialogTitle>
                  <DialogDescription>Send an announcement to all or selected employees</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Announcement title"
                        required
                        data-testid="announcement-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Write your announcement message..."
                        rows={5}
                        required
                        data-testid="announcement-message"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority</Label>
                        <Select
                          value={formData.priority}
                          onValueChange={(value) => setFormData({ ...formData, priority: value })}
                        >
                          <SelectTrigger data-testid="announcement-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="target">Target Audience</Label>
                        <Select
                          value={formData.target_type}
                          onValueChange={(value) => setFormData({ ...formData, target_type: value, target_employees: [] })}
                        >
                          <SelectTrigger data-testid="announcement-target">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Employees</SelectItem>
                            <SelectItem value="selected">Select Employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Multi-select Employee Picker */}
                    {formData.target_type === 'selected' && (
                      <div className="space-y-2">
                        <Label>Select Employees *</Label>
                        
                        {/* Selected employees chips */}
                        {formData.target_employees.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
                            {selectedEmployeeNames.map((name, idx) => (
                              <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                {name}
                                <X 
                                  className="w-3 h-3 cursor-pointer hover:text-red-500" 
                                  onClick={() => removeSelectedEmployee(formData.target_employees[idx])}
                                />
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {/* Search and dropdown */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                          <Input
                            placeholder="Search and select employees..."
                            value={employeeSearch}
                            onChange={(e) => {
                              setEmployeeSearch(e.target.value);
                              setShowEmployeeDropdown(true);
                            }}
                            onFocus={() => setShowEmployeeDropdown(true)}
                            className="pl-9"
                            data-testid="employee-multi-search"
                          />
                          {showEmployeeDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {filteredEmployees.length > 0 ? (
                                filteredEmployees.map((emp) => {
                                  const empUserId = emp.user_id || emp.id;
                                  const isSelected = formData.target_employees.includes(empUserId);
                                  return (
                                    <div
                                      key={emp.id}
                                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between ${isSelected ? 'bg-blue-50' : ''}`}
                                      onClick={() => toggleEmployeeSelection(empUserId)}
                                    >
                                      <span>{emp.name} ({emp.employee_code})</span>
                                      {isSelected && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="px-3 py-2 text-sm text-muted-foreground">No employees found</div>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formData.target_employees.length} employee(s) selected
                        </p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="announcement-submit">Create Announcement</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Admin View */}
        {isAdmin ? (
          <Card>
            <CardHeader>
              <CardTitle>All Announcements</CardTitle>
              <CardDescription>Manage company-wide announcements</CardDescription>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No announcements yet. Create one to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Read Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {announcements.map((ann) => (
                      <TableRow key={ann.id}>
                        <TableCell>
                          <div className="font-medium">{ann.title}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-xs">{ann.message}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={ann.priority === 'urgent' ? 'destructive' : 'secondary'}>
                            {ann.priority === 'urgent' && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {ann.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            <Users className="w-3 h-3 mr-1" />
                            {ann.target_type === 'all' ? 'All' : `${ann.target_employees?.length || 0} selected`}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(ann.created_at), 'dd/MM/yyyy HH:mm')}
                          <div className="text-xs text-muted-foreground">by {ann.created_by_name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{ann.read_count || 0}/{ann.target_count || '?'}</span>
                            <Button variant="ghost" size="sm" onClick={() => viewReads(ann)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setAnnouncementToDelete(ann);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Employee View */
          <Tabs defaultValue="unread" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="unread" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Unread ({unacknowledgedAnnouncements.length})
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                History ({acknowledgedAnnouncements.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="unread" className="space-y-4 mt-4">
              {unacknowledgedAnnouncements.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="text-muted-foreground">All caught up! No new announcements.</p>
                  </CardContent>
                </Card>
              ) : (
                unacknowledgedAnnouncements.map((ann) => (
                  <Card key={ann.id} className={ann.priority === 'urgent' ? 'border-red-300 border-2' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {ann.priority === 'urgent' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                            {ann.title}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(ann.created_at), 'dd MMM yyyy, HH:mm')}
                            <span>•</span>
                            by {ann.created_by_name}
                          </CardDescription>
                        </div>
                        <Badge variant={ann.priority === 'urgent' ? 'destructive' : 'secondary'}>
                          {ann.priority}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap mb-4">{ann.message}</p>
                      <Button onClick={() => handleAcknowledge(ann.id)} data-testid={`acknowledge-${ann.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Acknowledge
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="history" className="space-y-4 mt-4">
              {acknowledgedAnnouncements.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No acknowledged announcements yet.</p>
                  </CardContent>
                </Card>
              ) : (
                acknowledgedAnnouncements.map((ann) => (
                  <Card key={ann.id} className="opacity-75">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{ann.title}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1 text-xs">
                            <Clock className="w-3 h-3" />
                            {format(new Date(ann.created_at), 'dd MMM yyyy')}
                            <span>•</span>
                            Acknowledged on {format(new Date(ann.acknowledged_at), 'dd MMM yyyy')}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Acknowledged
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ann.message}</p>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* View Reads Dialog */}
      <Dialog open={viewReadsOpen} onOpenChange={setViewReadsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Read Status: {selectedAnnouncement?.title}</DialogTitle>
            <DialogDescription>
              {announcementReads.length} employee(s) have acknowledged this announcement
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto">
            {announcementReads.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No one has acknowledged yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Acknowledged At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {announcementReads.map((read) => (
                    <TableRow key={read.id}>
                      <TableCell>{read.user_name}</TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(read.acknowledged_at), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{announcementToDelete?.title}"? This will remove it from all employees' view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
